import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import type {
  BookingData,
  BookingStreamSummaryResponse,
  PaginatedDocumentResponse,
  PaginatedResponse,
  BookingResponse,
  ChurchDetails,
  ContentDetails,
  ConferenceDetails,
} from "../../types/view-booking";
import type { DocumentItem } from "../../../../types/documents";
import apiService from "../../../../service/Api";
import formatDate from "../../../../utils/FormatDate";

interface UseBookingDataReturn {
  bookingData: BookingData | null;
  documents: DocumentItem[];
  loading: boolean;
  error: string | null;
}

export const useBookingData = (): UseBookingDataReturn => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookingAndStreams = async () => {
      if (!id || !/^\d+$/.test(id)) {
        setError("Invalid booking ID");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const bookingResponse = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${id}/`,
        );
        const booking = bookingResponse.data;

        const streamsResponse = await apiService.get<PaginatedResponse>(
          `/api/streams/booking-stream-summary/?booking=${id}`,
        );
        const streamsData: BookingStreamSummaryResponse[] =
          streamsResponse.data.results || [];

        const documentsResponse =
          await apiService.get<PaginatedDocumentResponse>(
            `/api/bookings/documents/?booking=${id}`,
          );
        const documentsData: DocumentItem[] =
          documentsResponse.data.results || [];

        const dateOfBirth =
          formatDate(booking.deceased?.[0]?.date_of_birth) || "N/A";
        const completeness_score = booking?.completeness_score;
        const reference_number = booking?.reference_number;
        const dateOfDeath =
          formatDate(booking.deceased?.[0]?.date_of_death) || "N/A";

        const churchDetails: ChurchDetails | null =
          booking.service === "Church" && booking.services?.[0]
            ? {
                church_name: booking.services[0].church_name || "N/A",
                preacher_name: booking.services[0].preacher_name || "N/A",
                leader_preacher_name:
                  booking.services[0].leader_preacher_name || "N/A",
                date_of_service:
                  formatDate(booking.services[0].date_of_service) || "N/A",
                start_time: booking.services[0].start_time || "N/A",
                end_time: booking.services[0].end_time || "N/A",
                address_one: booking.services[0].venue.address_one || "N/A",
                address_two: booking.services[0].venue.address_two || "N/A",
                city: booking.services[0].venue.city || "N/A",
                title: booking.services[0].church_name || "N/A",
              }
            : null;

        const conferenceDetails: ConferenceDetails | null =
          booking.service === "Conference" && booking.events?.[0]
            ? {
                conference_title: booking.events[0].title || "N/A",
                start_date_of_event:
                  booking.events[0].start_date_of_event || "",
                end_date_of_event: booking.events[0].end_date_of_event,
                start_time: booking.events[0].start_time || "N/A",
                end_time: booking.events[0].end_time || "N/A",
                expected_audience_size:
                  booking.events[0].expected_audience_size,
                access_type: booking.events[0].access_type,
                streaming_platform: booking.events[0].streaming_platform,
                session_to_stream: booking.events[0].session_to_stream,
                record_stream: booking.events[0].record_stream,
                provide_on_demand_access:
                  booking.events[0].provide_on_demand_access,
                retention_duration: booking.events[0].retention_duration,
                require_custom_branding:
                  booking.events[0].require_custom_branding,
                branding_requirements: booking.events[0].branding_requirements,
                venue_name: booking.events[0].venue?.name || "N/A",
                address_one: booking.events[0].venue?.address_one || "N/A",
                address_two: booking.events[0].venue?.address_two || "N/A",
                city: booking.events[0].venue?.city || "N/A",
              }
            : null;

        const contentDetails: ContentDetails | null =
          booking.service === "Other"
            ? {
                title: booking.title || "N/A",
                date:
                  formatDate(booking.events?.[0]?.start_date_of_event) || "N/A",
                start_time: booking.services?.[0]?.start_time || "N/A",
                end_time: booking.services?.[0]?.end_time || "N/A",
                host_name: booking.events?.[0]?.hosts || "N/A",
                guest_name: booking.events?.[0]?.guests || "N/A",
              }
            : null;

        const contactBranch = booking.contact?.company_branch;
        const funeralHome = booking.funeral_home
          ? {
              name: booking.funeral_home.name,
              companyBranch: contactBranch
                ? [contactBranch.company?.name, contactBranch.name]
                    .filter(Boolean)
                    .join(" - ") || null
                : null,
            }
          : null;

        const mappedData: BookingData = {
          id: booking.id,
          status: booking.status,
          payment_status: booking.payment_status ?? undefined,
          service: booking.service || "N/A",
          booking_type: booking.booking_type,
          contacts: (booking.contacts || []).map((bc) => ({
            id: bc.id,
            contactId: bc.contact?.id,
            role: bc.role,
            name:
              `${bc.contact?.first_name || ""} ${
                bc.contact?.last_name || bc.contact?.name || ""
              }`.trim() || "N/A",
            email: bc.contact?.email || "N/A",
            phone: bc.contact?.phone || "N/A",
            contact_image: bc.contact?.contact_image || "/default-avatar.jpg",
          })),
          funeralHome,
          source_of_booking: booking.source_of_booking || "N/A",
          created_by: booking.created_by || null,
          last_updated_by: booking.last_updated_by || null,
          created_at: booking.created_at,
          updated_at: booking.updated_at,
          sales_rep: booking.sales_rep || null,
          organiser: booking.contact
            ? {
                id: booking.contact?.id,
                contact_type: "individual",
                name: `${booking.contact.first_name || ""} ${
                  booking.contact.last_name || booking.contact.name || ""
                }`.trim(),
                relation:
                  booking.deceased?.[0]?.relationship_to_deceased || "N/A",
                email: booking.contact.email || "N/A",
                contact_image:
                  booking.contact.contact_image || "/default-avatar.jpg",
                phone: booking.contact.phone || "N/A",
                address:
                  [booking.contact.address_one, booking.contact.address_two]
                    .filter(
                      (p): p is string =>
                        typeof p === "string" && p.trim().length > 0,
                    )
                    .join(", ") || "N/A",

                notes: booking.services?.[0]?.service_notes || "N/A",
                specialNotes: booking.services?.[0]?.special_request || "N/A",
                source_of_booking: booking.source_of_booking || undefined,
                referral_partner: booking.referral_partner ?? null,
              }
            : booking.company_contact
              ? {
                  id: booking.company_contact.id,
                  contact_type: "company",
                  name:
                    booking.company_contact.name ||
                    booking.company_contact.company_name ||
                    "N/A",
                  relation:
                    booking.company_contact.position || "Company Contact",
                  email: booking.company_contact.email || "N/A",
                  phone: booking.company_contact.phone || "N/A",
                  address:
                    booking.company_contact.address ||
                    booking.company_contact.company_name ||
                    "N/A",
                  notes: booking.services?.[0]?.service_notes || "N/A",
                  specialNotes: booking.services?.[0]?.special_request || "N/A",
                  source_of_booking: booking.source_of_booking || undefined,
                  referral_partner: booking.referral_partner ?? null,
                }
              : {
                  id: 0,
                  contact_type: "individual",
                  name: "N/A",
                  relation: "N/A",
                  email: "N/A",
                  phone: "N/A",
                  address: "N/A",
                  notes: "N/A",
                  specialNotes: "N/A",
                  source_of_booking: undefined,
                  referral_partner: null,
                },
          beloved: {
            name: booking.deceased?.[0]
              ? `${booking.deceased[0].first_name || ""} ${
                  booking.deceased[0].surname || ""
                }`.trim()
              : "N/A",
            avatar:
              booking.deceased?.[0]?.person_image || "/default-avatar.jpg",
            details: {
              second_name: booking.deceased?.[0]?.second_name || "N/A",
              dateOfBirth: dateOfBirth,
              dateOfDeath: dateOfDeath,
              gender: booking.deceased?.[0]?.gender || "N/A",
              nickname: booking.deceased?.[0]?.nickname || "N/A",
              religion: booking.deceased?.[0]?.religion || "N/A",
              denomination: booking.deceased?.[0]?.denomination || "N/A",
            },
          },
          churchDetails,
          contentDetails,
          conferenceDetails,
          completeness_score,
          reference_number,
          package: {
            name: booking.package_item?.name || "N/A",
            price: Number.parseFloat(booking.package_item?.price || "0"),
            items:
              booking.package_item?.services?.map((item) => ({
                id: item.id,
                name: item.name || item.description || "N/A",
                price: Number(item.price ?? 0),
              })) || [],
            total: booking.total || "0",
          },
          streams: streamsData.length
            ? streamsData.map((stream) => {
                // Find the best available venue source
                const venueSource = booking.services?.[0]?.venue;

                return {
                  id: stream.id,
                  title: stream.title || "N/A",
                  start: stream.start || "N/A",
                  end: stream.end || "N/A",
                  date: stream.date ? formatDate(stream.date) : "N/A",
                  template_id: stream.template_id ?? null,
                  venueName: venueSource?.name || "N/A",
                  addressOne: venueSource?.address_one || "N/A",
                  addressTwo: venueSource?.address_two || "",
                  suburb: venueSource?.suburb || "",
                  website_stream_link: stream.website_stream_link || "",

                  city: venueSource?.city || "N/A",
                  venueDetails: venueSource
                    ? {
                        id: venueSource.id,
                        name: venueSource.name,
                        address_one: venueSource.address_one,
                        address_two: venueSource.address_two,
                        suburb: venueSource.suburb,
                        city: venueSource.city,
                        files: venueSource.files || [],
                        layouts: venueSource.layouts || [],
                        parent: venueSource.parent ?? null,
                      }
                    : null,
                };
              })
            : ["Funeral", "Funeral_Home", "Church", "Other"].includes(
                  booking.service,
                )
              ? [
                  {
                    title: booking.deceased?.[0]
                      ? `${booking.deceased[0].first_name || ""} ${
                          booking.deceased[0].surname || ""
                        }`.trim()
                      : booking.title || "N/A",
                    start: booking.services?.[0]?.start_time || "N/A",
                    end: booking.services?.[0]?.end_time || "N/A",

                    date:
                      formatDate(booking.services?.[0]?.date_of_service) ||
                      "N/A",
                    addressOne: booking.services?.[0]?.service_address || "N/A",
                    addressTwo:
                      booking.services?.[0]?.service_location || "N/A",
                    suburb: booking.services?.[0]?.service_city || "N/A",
                    city: booking.city || "N/A",
                  },
                ]
              : [],
        };

        setBookingData(mappedData);
        setDocuments(documentsData);
        setError(null);
      } catch (err: unknown) {
        let errorMessage =
          "Failed to load booking or streams. Please try again.";
        const errorResponse = (
          err as {
            response?: { status?: number; data?: { detail?: string } };
            message?: string;
          }
        )?.response;
        if (errorResponse?.status === 500) {
          errorMessage =
            "Server error: Unable to retrieve data. Please try again later or contact support.";
        } else if (errorResponse?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (errorResponse?.status === 403) {
          errorMessage =
            "Permission denied: You do not have access to this booking.";
          navigate("/bookings");
        } else if (errorResponse?.data?.detail) {
          errorMessage = errorResponse.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error(
          "Fetch error:",
          errorResponse?.data,
          (err as { message?: string })?.message,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBookingAndStreams();
  }, [id, navigate]);
  return { bookingData, documents, loading, error };
};
